import { useState, useRef, useEffect } from "react";

// Drawer of one-click sample indices shown under TIME SERIES. `query` is what
// gets passed to addSeries (resolved via /api/series-resolve); `label` is the
// short display text on the drawer chip.
const SAMPLE_INDICES = [
  { label: "S&P 500", query: "S&P 500" },
  { label: "Dow", query: "Dow Jones" },
  { label: "Nasdaq", query: "nasdaq" },
  { label: "WTI", query: "WTI Crude" },
  { label: "Brent", query: "Brent Crude" },
  { label: "Gold", query: "gold" },
  { label: "Copper", query: "Copper" },
  { label: "Wheat", query: "Wheat" },
  { label: "Lumber", query: "Lumber" },
  { label: "BTC", query: "bitcoin" },
  { label: "EUR/USD", query: "EUR/USD" },
  { label: "10Y Treasury", query: "10Y Treasury" },
  { label: "World Pop.", query: "World Population" },
  { label: "Unemployment", query: "US Unemployment" },
  { label: "Tuition CPI", query: "US College Tuition CPI" },
  { label: "US Gas Price", query: "US Gas Price" },
  { label: "Life Expectancy", query: "US Life Expectancy" },
];

// Canonical names of signal-library concepts that were removed (made
// unresolvable) server-side. A persisted drawer order (localStorage, keyed per
// user) from before a removal still carries the dead chip — and removing it from
// SAMPLE_INDICES / the server doesn't touch saved state — so clicking it now
// throws SeriesNotFoundError. Prune these whenever a saved order is loaded. This
// is a denylist (not "filter to SAMPLE_INDICES") so user-added custom chips
// (manual query / CSV upload / library suggestions) survive.
//
// Every add path stores the RESOLVED canonical name as both label and query
// (`{label:s.name,query:s.name}` / `r.name`), never a raw alias, so matching the
// canonical names here is sufficient. Only fully-removed concepts belong here —
// concepts that were *repointed* to a live source (US Poverty Rate, 2Y Treasury)
// still resolve and must NOT be filtered.
//
// All removed in #1456: CO2 ppm (Scripps retired its Mauna Loa daily CSV) plus
// four FRED mirrors whose ids 404'd with no maintained source (US Birth Rate,
// US Immigration, Global Trade, Baltic Dry Index).
const REMOVED_QUERIES = new Set([
  "co2 ppm",
  "us birth rate",
  "us immigration",
  "global trade",
  "baltic dry index",
]);

/**
 * Sanitize a persisted drawer order: drop any chip that is malformed or backs a
 * removed signal. A well-formed chip is an object with string `label` and
 * `query`; anything else is dropped so the drawer never renders a chip that
 * would crash on `idx.query.toLowerCase()` (SignalLibraryDrawer) or resolve to a
 * dead series. All other chips (including user-added custom ones) are kept.
 * Case-insensitive on both fields. Always returns an array (empty if `order`
 * isn't one). Exported for unit testing.
 *
 * @param {Array<{label:string,query:string}>} order
 * @returns {Array<{label:string,query:string}>}
 */
export function pruneRemovedIndices(order) {
  if (!Array.isArray(order)) return [];
  return order.filter(
    (i) =>
      i &&
      typeof i.query === "string" &&
      typeof i.label === "string" &&
      !REMOVED_QUERIES.has(i.query.toLowerCase()) &&
      !REMOVED_QUERIES.has(i.label.toLowerCase()),
  );
}

/**
 * Drawer-indices domain: chip order state, manual-query + drag state, CSV
 * input ref, and the two me-keyed localStorage persistence effects (LOAD on
 * me change, SAVE on drawerOrder change).
 *
 * @param {{ me: object|null }} args
 * @returns {{ drawerOrder, setDrawerOrder, manualQuery, setManualQuery, draggedChip, setDraggedChip, csvInputRef }}
 */
export function useDrawerIndices({ me }) {
  // Drawer chip order — persisted per user in localStorage (keyed by Zitadel
  // sub, or "guest" before sign-in). Defaults to SAMPLE_INDICES on first load.
  // The me-keyed load is done in a useEffect below; this initial state is the
  // unauthenticated default until /api/me resolves.
  const [drawerOrder, setDrawerOrder] = useState(() => {
    try {
      const saved = localStorage.getItem("sooth-drawer-indices-guest");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const pruned = pruneRemovedIndices(parsed);
          if (pruned.length) return pruned;
        }
      }
    } catch (_e) {
      /* localStorage unavailable */
    }
    return SAMPLE_INDICES.map((i) => ({ label: i.label, query: i.query }));
  });
  const [manualQuery, setManualQuery] = useState("");
  const [draggedChip, setDraggedChip] = useState(null);
  // Hidden file input for the CSV upload button in the drawer's Add Indices bar.
  const csvInputRef = useRef(null);
  // Drawer-indices persistence: when the signed-in user becomes known (or
  // signs out), swap the drawer to that account's saved order. Defaults to
  // SAMPLE_INDICES on first sign-in.
  const drawerLoadedForRef = useRef(null);
  useEffect(() => {
    const key = me?.sub ? `sooth-drawer-indices-${me.sub}` : "sooth-drawer-indices-guest";
    if (drawerLoadedForRef.current === key) return;
    drawerLoadedForRef.current = key;
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const pruned = pruneRemovedIndices(parsed);
          if (pruned.length) {
            setDrawerOrder(pruned); // eslint-disable-line react-hooks/set-state-in-effect
            return;
          }
        }
      }
    } catch (_e) {
      /* localStorage unavailable */
    }
    setDrawerOrder(SAMPLE_INDICES.map((i) => ({ label: i.label, query: i.query })));
  }, [me?.sub]);
  // Save drawer order to the active user's localStorage slot on every change.
  useEffect(() => {
    const key = me?.sub ? `sooth-drawer-indices-${me.sub}` : "sooth-drawer-indices-guest";
    try {
      localStorage.setItem(key, JSON.stringify(drawerOrder));
    } catch (_e) {
      /* localStorage unavailable */
    }
  }, [drawerOrder, me?.sub]);

  return {
    drawerOrder,
    setDrawerOrder,
    manualQuery,
    setManualQuery,
    draggedChip,
    setDraggedChip,
    csvInputRef,
  };
}
